const PRINT_WIDTH = 31

export const getDataLine = (
  value: string | null | undefined,
  label: string | null = null,
  centered = false
) => {
  if (!value) {
    return ""
  }

  const labelString = label ? `${label}: ` : ""

  if (centered) {
    const content = labelString + value
    const paddingLength = Math.floor(
      Math.max(PRINT_WIDTH - content.length, 0) / 2
    )
    return (
      " ".repeat(paddingLength) +
      content +
      " ".repeat(paddingLength) +
      "\n"
    )
  }

  // Else right align
  const spacingLength = Math.max(
    PRINT_WIDTH - labelString.length - value.length,
    0
  )
  return (
    labelString + " ".repeat(spacingLength) + value + "\n"
  )
}

/**
 * @param label Label to print on left
 * @param value1 Value1 to print in middle
 * @param value2 Value2 to print on the right
 */
export const getDoubleDataLine = (
  label: string,
  value1: string,
  value2: string
) => {
  const value1PositionInChars = Math.floor(PRINT_WIDTH / 2)

  const spaceForLabelInChars = value1PositionInChars - 1

  const trimmedLabel = label.slice(0, spaceForLabelInChars)

  const spaceForValue1InChars =
    PRINT_WIDTH - value1PositionInChars + 1

  const trimmedValue1 = value1.slice(
    0,
    spaceForValue1InChars
  )

  const spaceForValue2InChars = Math.max(
    spaceForValue1InChars - value1.length,
    0
  )

  const trimmedValue2 = value2.slice(
    0,
    spaceForValue2InChars
  )

  return (
    trimmedLabel +
    " ".repeat(spaceForLabelInChars - trimmedLabel.length) +
    trimmedValue1 +
    " ".repeat(
      spaceForValue2InChars - trimmedValue2.length
    ) +
    trimmedValue2 +
    "\n"
  )
}

export const getBlankLines = (count: number) =>
  "\n".repeat(count)

/**
 * Formats an array of receipt lines into a single string ready for print.
 *
 * Receipt lines can be of any length.
 *
 * A 'token' is a substring separated by 2 or more spaces from neighbouring substrings.
 * Lines containing 1 'token' are centered, and lines with 2 or more are padded
 * with space between each token.
 *
 * @param rawReceipt Array of receipt lines
 * @param header A custom header to print before the receipt
 * @returns a print-ready string
 */
export const formatReceipt = (
  rawReceipt: Array<string>,
  header?: string
) =>
  rawReceipt.reduce(
    (receipt, newLineRaw) => {
      // Splits line into "tokens" separated by two or more spaces
      const newLineTokens = newLineRaw
        .trim()
        .split(/\s{2,}/)
      const tokenCount = newLineTokens.length // >= 1
      const totalTokenLength = newLineTokens.reduce(
        (acc, token) => acc + token.length,
        0
      )
      const remainingSpace = PRINT_WIDTH - totalTokenLength
      const minimumWhitespaceRequired = 2 * (tokenCount - 1)

      if (minimumWhitespaceRequired > remainingSpace) {
        // Line is too long for device, print it as-is and let printer handle wrapping
        return receipt + newLineRaw + "\n"
      }

      if (tokenCount == 1) {
        const sidePaddingLength = Math.floor(
          remainingSpace / 2
        )
        const newLine =
          " ".repeat(sidePaddingLength) +
          newLineTokens[0] +
          " ".repeat(sidePaddingLength)
        return receipt + newLine + "\n"
      }

      // TokenCount >= 2
      const internalPaddingLength = Math.floor(
        remainingSpace / (tokenCount - 1)
      )
      const extraPadding =
        remainingSpace -
        internalPaddingLength * (tokenCount - 1)
      const newLineString = newLineTokens.reduce(
        (acc, token, index) =>
          acc +
          (index === 1
            ? " ".repeat(
                internalPaddingLength + extraPadding
              ) + token
            : " ".repeat(internalPaddingLength) + token)
      )
      return receipt + newLineString + "\n"
    },
    header ?? getDataLine("** takepayments+ **", null, true)
  )
